/**
 * Colours for the pipeline diagram.
 *
 * **Every map here is keyed by an open `string`, not by a union.** Lanes are
 * deployment configuration (`work.trends.lanes`) and this file inherited a
 * `fulfillment` lane the current backend does not produce, while the health
 * map carried `decline` that the frontend's own union had omitted. Typing
 * them as closed records made the file fail to compile against reality in
 * both directions, which is the wrong trade for a colour lookup: a missing
 * key falls back, and a spare key costs nothing.
 *
 * Lane fills are taken from `lane.color` on the route instead — see
 * `PipelineDiagram`. What stays here is everything the route does not
 * colour: dwell tiers, age bands, hand-off severity, surfaces, type.
 */
// Color palette for the Team Trends tab. Colors are fixed per lane
// category so the reader's "green = fulfillment, amber = support,
// purple = project, blue = core" mental model stays stable across
// all trend visualisations (ribbons, avatars, sparklines, bars).
//
// Kept in one module so updating a palette is a single edit.


// ---------------------------------------------------------------------
// Lane palette
// ---------------------------------------------------------------------

// ``LANE_COLORS`` is keyed by both v1 ``LaneCategory`` slugs
// (``fulfillment``/``support``/``project``/``core``/``unassigned``) and
// v2 short slugs (``td``/``sup``/``proj``/``core``/``unassigned``) so
// both pipelines can use the same accent palette without duplication.
export const LANE_COLORS: Record<string, string> = {
  // v1 slugs
  fulfillment: "#3dd68c",
  support:     "#f0b449",
  project:     "#c488ef",
  core:        "#6d7bef",
  unassigned:  "#555970",
  // v2 slugs
  td:   "#3dd68c",
  sup:  "#f0b449",
  proj: "#c488ef",
};

// Short display labels used in compact captions (e.g. stacked bar
// tiny-labels). Longer human labels live on the server-side Lane.label
// field so the two don't collide.
export const LANE_SHORT_LABELS: Record<string, string> = {
  fulfillment: "Ful",
  support:     "Sup",
  project:     "Proj",
  core:        "Core",
  unassigned:  "Unassigned",
};

// ---------------------------------------------------------------------
// Dwell tier fills
// ---------------------------------------------------------------------
//
// Cold cells sit on a dark panel so they recede; warm cells glow a
// muted amber; hot cells scream in red. The fill colours are chosen
// to remain readable against the ``#1c1f2e`` lane background while
// the stroke adds enough contrast for the tier to be legible even
// on small cells.

export const DWELL_FILL: Record<string, string> = {
  cold: "#161922",
  warm: "rgba(240,180,73,0.18)",
  hot:  "rgba(239,95,95,0.20)",
};

export const DWELL_STROKE: Record<string, string> = {
  cold: "#252836",
  warm: "rgba(240,180,73,0.4)",
  hot:  "rgba(239,95,95,0.6)",
};

// Text colour for the count inside a cell. Cold uses the normal
// foreground; warm/hot use the matching tier accent so the reader
// can spot an outlier at a glance.
export const DWELL_COUNT_COLOR: Record<string, string> = {
  cold: "#e2e4ea",
  warm: "#f0b449",
  hot:  "#ef5f5f",
};

// ---------------------------------------------------------------------
// Throughput health dot
// ---------------------------------------------------------------------

export const HEALTH_COLOR: Record<string, string> = {
  healthy:  "#3dd68c",
  watch:    "#f0b449",
  overload: "#ef5f5f",
  decline:  "#ef5f5f",
};

export const HEALTH_LABEL: Record<string, string> = {
  healthy:  "healthy",
  watch:    "watch",
  overload: "overload",
  decline:  "declining",
};

// ---------------------------------------------------------------------
// Shared visual tokens
// ---------------------------------------------------------------------

export const COLORS = {
  pageBg:       "#0f1117",
  cardBg:       "#161922",
  laneBg:       "#1c1f2e",
  border:       "#252836",
  borderMuted:  "rgba(37,40,54,0.6)",
  textPrimary:  "#e2e4ea",
  textMuted:    "#8b8fa3",
  textDim:      "#555970",
  textFaint:    "#3a3d50",
  accent:       "#6d7bef",
  alert:        "#ef5f5f",
  success:      "#3dd68c",
};

// ---------------------------------------------------------------------
// v2 tokens (Team Trends tab v2)
// ---------------------------------------------------------------------

// Dwell tier fills (v2 — slightly darker/stronger than v1 ``DWELL_FILL``)
export const DWELL_COLORS = {
  cold: { fill: "#10131c", stroke: "#222634" },
  warm: { fill: "rgba(240,180,73,0.12)", stroke: "rgba(240,180,73,0.30)" },
  hot:  { fill: "rgba(239,95,95,0.12)",  stroke: "rgba(239,95,95,0.40)" },
} as const;

// Age strip bands
export const AGE_COLORS = {
  fresh: "#3dd68c",   // opacity applied by caller
  mid:   "#f0b449",
  stale: "#ef5f5f",
} as const;

export const AGE_OPACITY = {
  fresh: 0.60,
  mid:   0.70,
  stale: 0.80,
} as const;

// Lane ribbon opacity
export const LANE_RIBBON_OPACITY = 0.38;

// Surfaces
export const SURFACES = {
  page:   "#0b0e14",
  panel:  "#151820",
  lane:   "#131620",
  track:  "#0e1118",
  cell:   "#10131c",
  border: "#222634",
} as const;

// Handoff
export const HANDOFF_COLORS = {
  norm: "#555970",
  warn: "#f0b449",
  hot:  "#ef5f5f",
} as const;

// Rework / drift
export const ALERT_COLOR = "#ef5f5f";

// ---------------------------------------------------------------------
// v2 visual-tune tokens (2026-04-17)
// ---------------------------------------------------------------------

export const TRENDS_SURFACES = {
  cardFill: "#12151c",
  cellFill: "#12151c",  // same shade as cardFill by design — kept as a separate token so a future hover/active state can diverge
  divider: "#2e3240",
  zeroLine: "#2e3240",
  baselineLine: "#2e3240",
} as const;

export type TrendsTypeSpec = {
  fontSize: number;
  fontWeight?: number;
  fill: string;
  letterSpacing?: string;
};

export const TRENDS_TYPE: Record<string, TrendsTypeSpec> = {
  laneLabel:     { fontSize: 10, fontWeight: 700, fill: "#e6e9f0" },
  laneSubLabel:  { fontSize: 6,  fontWeight: 400, fill: "#6b6f82" },
  cellCount:     { fontSize: 9,  fontWeight: 700, fill: "#e6e9f0" },
  columnHeader:  { fontSize: 7,  fontWeight: 600, fill: "#c0c4d1", letterSpacing: "0.06em" },
  sectionHeader: { fontSize: 7,  fontWeight: 600, fill: "#6b6f82", letterSpacing: "0.14em" },
  caption:       { fontSize: 6,  fill: "#6b6f82" },
  metricHint:    { fontSize: 5,  fill: "#6b6f82" },
};

export const TRENDS_TREND_COLORS = {
  good: "#7fe3b0",
  bad:  "#f5a584",
  neutral: "#d4d7e0",
  muted:   "#6b6f82",
  anomalyStroke:     "#d9643a",
  bottleneckOutline: "#d9643a",
  bottleneckCount:   "#f5a584",
} as const;
